<template>
  <div
    class="dealer-locator-map py-4 lg:py-8"
    :class="[model.background, model.textColor ? model.textColor : 'text-[color:var(--color-body-text)]']"
  >
    <div class="mx-auto w-full max-w-screen-2xl px-5 md:px-12">
      <h2 class="mb-5 text-2xl font-bold lg:text-3xl">{{ model.title }}</h2>
      <div class="map-wrapper relative">
        <div
          class="filter-button align-center absolute bottom-[20px] left-[50%] z-20 flex w-[225px] translate-x-[-50%] justify-center rounded-full bg-[color:var(--color-secondary-500)] p-2 md:bottom-[unset] md:top-[10px]"
        >
          <label class="relative inline-flex cursor-pointer items-center">
            <input type="checkbox" class="peer sr-only" @click="onChange($event)" />
            <div
              class="after:border-gray-300 peer h-6 w-11 rounded-full bg-neutral-200 after:absolute after:left-[2px] after:top-[2px] after:size-5 after:rounded-full after:border after:bg-white after:transition-all after:content-[''] peer-checked:bg-[color:var(--color-primary-500)] peer-checked:after:translate-x-full peer-checked:after:border-white"
            ></div>
            <span class="ml-3 text-sm font-medium text-white">Alleen online dealers</span>
          </label>
        </div>
        <div ref="map" class="google-map"></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { Loader } from "@googlemaps/js-api-loader";
import { onMounted, ref } from "vue";
import { useDealersForLocator } from "@/shared/company";
import type { Organization } from "@/core/api/graphql/types";
import type { OrganizationItem } from "@/shared/static-content";
import type { Ref } from "vue";
import { useBopis } from "@/shared/checkout/composables/useBopis";

defineProps({
  model: {
    type: Object,
    required: true,
  },

  settings: {
    type: Object,
    required: true,
  },
});

const { fetchDealersForLocator, dealersForLocator } = useDealersForLocator();
const { apiKey } = useBopis();

const loader = new Loader({
  apiKey: (apiKey as string) || "",
  version: "weekly",
  libraries: ["places"],
});

const map = ref([]);
const dealerLocations: Ref<OrganizationItem[]> = ref([]);

const mapOptions = {
  center: {
    lat: 52.132633,
    lng: 5.2912659999999505,
  },
  zoom: 7,
};

onMounted(async () => {
  await fetchDealersForLocator();

  dealerLocations.value = dealersForLocator.value
    .filter((item: any) => item.dynamicProperties[0].value != null)
    .map(getOrganizationItem);

  reloadMarkers();
});

function getOrganizationItem(organization: Organization) {
  return {
    title: organization.name,
    lat: organization.dynamicProperties.filter((item: any) => item.name == "Latitude").map((lat: any) => +lat.value)[0],
    long: organization.dynamicProperties
      .filter((item: any) => item.name == "Longitude")
      .map((long: any) => +long.value)[0],
    isOnline: organization.dynamicProperties
      .filter((item: any) => item.name == "Online dealer")
      .map((long: any) => long.value)[0],
    city: organization.defaultBillingAddress?.city ?? "",
    street: organization.defaultBillingAddress?.line1 ?? "",
    email: organization.defaultBillingAddress?.email ?? "",
    phone: organization.defaultBillingAddress?.phone ?? "",
    postalCode: organization.defaultBillingAddress?.postalCode ?? "",
  };
}

function onChange(event: any) {
  if (event.target.checked) {
    dealerLocations.value = dealerLocations.value.filter((item: OrganizationItem) => item.isOnline === true);
  } else {
    dealerLocations.value = dealersForLocator.value
      .filter((item: any) => item.dynamicProperties[0].value != null)
      .map(getOrganizationItem);
  }
  reloadMarkers();
}

function reloadMarkers() {
  loader
    .load()
    .then((google: any) => {
      const googleMap: any = new google.maps.Map(map.value, mapOptions);

      const image = "/static/images/location-marker.png?raw=true";

      dealerLocations.value.forEach((item: OrganizationItem) => {
        const { LatLng } = google.maps;

        const contentString =
          '<div id="content">' +
          `<h2 class="google-map__title">${item.title}</h2>` +
          '<div id="google-map__content">' +
          (item.street !== "" ? `<p>${item.street}</p>` : "") +
          (item.postalCode !== "" && item.street !== "" ? `<p>${item.postalCode}, ${item.city}</p>` : "") +
          (item.email !== ""
            ? `<p>E: <a target="_blank" class="google-map__link" href="mailto:${item.email}">${item.email}</a></p>`
            : "") +
          (item.phone !== ""
            ? `<p>T: <a target="_blank" class="google-map__link" href="tel:${item.phone}">${item.phone}</a></p>`
            : "") +
          "</div>" +
          "</div>";

        if (item.lat && !isNaN(item.lat) && item.long && !isNaN(item.long)) {
          const infowindow = new google.maps.InfoWindow({
            content: contentString,
            ariaLabel: item.title,
          });

          const marker = new google.maps.Marker({
            icon: image,
            position: new LatLng(item.lat, item.long),
            map: googleMap,
          });

          marker.addListener("click", () => {
            infowindow.open({
              anchor: marker,
              map: googleMap,
            });
          });
        }
      });
    })
    .catch((e) => {
      console.log(e);
    });
}
</script>

<style lang="scss">
.google-map {
  width: 100%;
  height: 550px;

  &__title {
    font-size: 20px;
    font-weight: bold;
    margin-bottom: 10px;
  }

  &__content {
  }

  &__link {
    @apply text-[color:var(--color-secondary)-500];
  }
}
</style>
